import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Skeleton,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useSidebar } from "../../components/SidebarContext";
import { getApiUrl, getApiHeaders } from "../../config/api";

const PeakTimeLostItems = ({ isTabView }) => {
  const [timeSlotData, setTimeSlotData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPeakTimeData();
  }, []);

  const fetchPeakTimeData = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl("found-item/list"), {
        headers: getApiHeaders()
      });
      
      if (!response.ok) throw new Error("Failed to fetch items");
      
      const data = await response.json();
      const items = data.items || [];
      
      // Group items by 2-hour time slots
      const timeSlots = {
        "8 AM - 10 AM": [],
        "10 AM - 12 PM": [],
        "12 PM - 2 PM": [],
        "2 PM - 4 PM": [],
        "4 PM - 6 PM": [],
        "6 PM - 8 PM": [],
      };
      
      items.forEach(item => {
        if (!item.time_found) return;
        
        const hour = parseInt(item.time_found.split(':')[0]);
        let slot = "";
        
        if (hour >= 8 && hour < 10) slot = "8 AM - 10 AM";
        else if (hour >= 10 && hour < 12) slot = "10 AM - 12 PM";
        else if (hour >= 12 && hour < 14) slot = "12 PM - 2 PM";
        else if (hour >= 14 && hour < 16) slot = "2 PM - 4 PM";
        else if (hour >= 16 && hour < 18) slot = "4 PM - 6 PM";
        else if (hour >= 18 && hour < 20) slot = "6 PM - 8 PM";
        
        if (slot && timeSlots[slot]) {
          timeSlots[slot].push(item);
        }
      });
      
      // Calculate stats for each time slot
      const formattedData = Object.entries(timeSlots).map(([slot, items]) => {
        // Count item types
        const typeCounts = items.reduce((acc, item) => {
          const type = item.item_type || "Unknown";
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {});
        
        // Count locations
        const locationCounts = items.reduce((acc, item) => {
          const location = item.location_found || "Unknown";
          acc[location] = (acc[location] || 0) + 1;
          return acc;
        }, {});
        
        // Find most common
        const mostCommonType = Object.keys(typeCounts).length > 0
          ? Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0][0]
          : "N/A";
          
        const mostAffectedLocation = Object.keys(locationCounts).length > 0
          ? Object.entries(locationCounts).sort((a, b) => b[1] - a[1])[0][0]
          : "N/A";
        
        return {
          timeSlot: slot,
          totalLostReports: items.length.toString(),
          mostCommonLostItem: mostCommonType,
          mostAffectedLocation: mostAffectedLocation,
        };
      }).filter(slot => parseInt(slot.totalLostReports) > 0); // Only show slots with data
      
      setTimeSlotData(formattedData);
    } catch (error) {
      console.error("Error fetching peak time data:", error);
    } finally {
      setLoading(false);
    }
  };

  const { isSidebarOpen } = useSidebar();
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;

  if (isTabView) {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <TableContainer sx={{ flex: 1, bgcolor: "#fbffe8", borderRadius: "15px", border: "2px solid #516d4f" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><Typography variant="h6" fontWeight="500" color="#516d4f">Time Slot</Typography></TableCell>
                <TableCell><Typography variant="h6" fontWeight="500" color="#516d4f">Total Lost Reports</Typography></TableCell>
                <TableCell><Typography variant="h6" fontWeight="500" color="#516d4f">Most Common Lost Item</Typography></TableCell>
                <TableCell><Typography variant="h6" fontWeight="500" color="#516d4f">Most Affected Location</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton variant="text" width="120px" height={30} /></TableCell>
                    <TableCell><Skeleton variant="text" width="100px" height={30} /></TableCell>
                    <TableCell><Skeleton variant="text" width="150px" height={30} /></TableCell>
                    <TableCell><Skeleton variant="text" width="150px" height={30} /></TableCell>
                  </TableRow>
                ))
              ) : (
                timeSlotData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell><Typography variant="h6" fontWeight="500" color="#516d4f">{row.timeSlot}</Typography></TableCell>
                    <TableCell><Typography variant="h6" fontWeight="400" color="#516d4f">{row.totalLostReports}</Typography></TableCell>
                    <TableCell><Typography variant="h6" fontWeight="400" color="#516d4f">{row.mostCommonLostItem}</Typography></TableCell>
                    <TableCell><Typography variant="h6" fontWeight="400" color="#516d4f">{row.mostAffectedLocation}</Typography></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box
      sx={{
  transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        bgcolor: "#fbffe8",
        display: "flex",
        justifyContent: "center",
        width: "100%",
        maxWidth: isSidebarOpen ? (isMobile ? "calc(100vw - 200px)" : "calc(100vw - 300px)") : "100vw",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: "#fbffe8",
          width: "100%",
          maxWidth: "1920px",
          height: "1080px",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: "1664px",
            height: "924px",
            top: "82px",
            left: "147px",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: "1664px",
              height: "913px",
              top: "11px",
              left: 0,
            }}
          >
            <Paper
              elevation={4}
              sx={{
                position: "absolute",
                width: "1664px",
                height: "848px",
                top: "65px",
                left: 0,
                bgcolor: "#e1ecc8",
                borderRadius: "20px",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: "1102px",
                  height: "723px",
                  top: "62px",
                  left: "60px",
                  bgcolor: "#fbffe8",
                  borderRadius: "20px",
                  border: "2px solid #516d4f",
                }}
              >
                <TableContainer sx={{ height: "100%" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography
                            variant="h5"
                            fontFamily="Poppins"
                            fontWeight="500"
                            color="#516d4f"
                            textAlign="left"
                          >
                            Time Slot
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="h5"
                            fontFamily="Poppins"
                            fontWeight="500"
                            color="#516d4f"
                            textAlign="center"
                          >
                            Total Lost Reports
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="h5"
                            fontFamily="Poppins"
                            fontWeight="500"
                            color="#516d4f"
                            textAlign="center"
                          >
                            Most Common Lost Item
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="h5"
                            fontFamily="Poppins"
                            fontWeight="500"
                            color="#516d4f"
                            textAlign="center"
                          >
                            Most Affected Location
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {timeSlotData.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography
                              variant="h5"
                              fontFamily="Poppins"
                              fontWeight="500"
                              color="#516d4f"
                            >
                              {row.timeSlot}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="h5"
                              fontFamily="Poppins"
                              fontWeight="400"
                              color="#516d4f"
                            >
                              {row.totalLostReports}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="h5"
                              fontFamily="Poppins"
                              fontWeight="400"
                              color="#516d4f"
                            >
                              {row.mostCommonLostItem}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="h5"
                              fontFamily="Poppins"
                              fontWeight="400"
                              color="#516d4f"
                            >
                              {row.mostAffectedLocation}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  width: "394px",
                  height: "281px",
                  top: "498px",
                  left: "1243px",
                }}
              >
                <Box
                  component="img"
                  src="https://c.animaapp.com/m8zz4eg8bQYAOw/img/zr71-cls-209.png"
                  alt="Chart grid"
                  sx={{
                    position: "absolute",
                    width: "315px",
                    height: "226px",
                    top: "23.5px",
                    left: "47px",
                  }}
                />
                <Box
                  component="img"
                  src="https://c.animaapp.com/m8zz4eg8bQYAOw/img/zr71-cls-210.png"
                  alt="Bar chart"
                  sx={{
                    position: "absolute",
                    width: "289px",
                    height: "211px",
                    top: "27px",
                    left: "60px",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    width: "284px",
                    height: "11px",
                    top: "259px",
                    left: "67px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontFamily="Inter"
                    color="#828898"
                    sx={{ width: "39px", textAlign: "center" }}
                    >
                      8 AM - ...
                    </Typography>
                    <Typography
                      variant="caption"
                      fontFamily="Inter"
                      color="#828898"
                      sx={{ width: "41px", textAlign: "center" }}
                    >
                      12 AM -...
                    </Typography>
                    <Typography
                      variant="caption"
                      fontFamily="Inter"
                      color="#828898"
                      sx={{ width: "39px", textAlign: "center" }}
                    >
                      3 PM - ...
                    </Typography>
                    <Typography
                      variant="caption"
                      fontFamily="Inter"
                      color="#828898"
                      sx={{ width: "39px", textAlign: "center" }}
                    >
                      4 PM - ...
                    </Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    fontFamily="Poppins"
                    color="#a0c49d"
                    sx={{
                      position: "absolute",
                      transform: "rotate(-90deg)",
                      transformOrigin: "center",
                      width: "348px",
                      top: "130px",
                      left: "-100px",
                    }}
                  >
                    Number of Lost Items
                  </Typography>
                </Box>
              </Paper>
  
              <Typography
                variant="h4"
                fontFamily="Poppins"
                fontWeight="bold"
                color="#516d4f"
                sx={{ position: "absolute", top: 0, left: "3.5px" }}
              >
                Peak Time of Lost Items
              </Typography>
            </Box>
  
            <Box
              sx={{
                position: "absolute",
                width: "379px",
                height: "60px",
                top: 0,
                left: "1285px",
              }}
            >
              <Autocomplete
                options={[]}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Search"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#516d4f" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <ArrowDropDownIcon
                            sx={{ color: "#516d4f", fontSize: 40 }}
                          />
                        </InputAdornment>
                      ),
                      sx: {
                        bgcolor: "rgba(81, 109, 79, 0.6)",
                        borderRadius: "30px",
                        height: "58px",
                        "& fieldset": { border: "none" },
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
  
          <Typography
            variant="h5"
            fontFamily="Poppins"
            fontWeight="bold"
            color="#516d4f"
            sx={{ position: "absolute", top: "8px", left: "167px" }}
          >
            UFind
          </Typography>
  
          <IconButton
            sx={{
              position: "absolute",
              top: "26px",
              left: "62px",
              color: "#516d4f",
            }}
          >
            <MenuIcon fontSize="large" />
          </IconButton>
        </Box>
      </Box>
    );
  };

export default PeakTimeLostItems;